"use client";

import { useRouter } from "next/navigation";
import { useState, useTransition } from "react";
import { Segmented } from "@/components/ui/design-system/segmented";
import { setCreatorLocale } from "@/i18n/creator/actions";
import {
  CREATOR_LOCALE_ENDONYMS,
  CREATOR_LOCALES,
  type CreatorLocale,
  isCreatorLocale,
} from "@/i18n/creator/locales";
import {
  useCreatorLocale,
  useCreatorTranslations,
} from "@/i18n/creator/useCreatorTranslations";
import { SettingRow } from "./settingsRow";

/**
 * The creator's language, as a row in Settings.
 *
 * It sets the cookie the server already reads first, then refreshes, so the
 * layouts renegotiate and hand down the new catalogue without a full reload:
 * the sheet stays open and nothing typed elsewhere is lost. The segment moves
 * the moment it is pressed rather than when the new copy arrives, so the
 * choice reads as taken while the page catches up.
 *
 * Stored in this browser only, like Appearance, until the creator record
 * carries a language of its own (phase 7).
 */
export function LanguageSetting() {
  const t = useCreatorTranslations("settings");
  const locale = useCreatorLocale();
  const router = useRouter();
  const [pending, startTransition] = useTransition();
  const [chosen, setChosen] = useState<CreatorLocale | null>(null);
  const current: CreatorLocale = isCreatorLocale(locale) ? locale : "en";

  return (
    <SettingRow
      label={t("language.label")}
      hint={t("deviceOnly")}
      control={
        <div className="flex sm:justify-end" aria-busy={pending || undefined}>
          <Segmented
            name="creator-language"
            legend={t("language.label")}
            value={pending && chosen ? chosen : current}
            onChange={(next) => {
              setChosen(next);
              startTransition(async () => {
                await setCreatorLocale(next);
                router.refresh();
              });
            }}
            options={CREATOR_LOCALES.map((id) => ({
              id,
              name: CREATOR_LOCALE_ENDONYMS[id],
            }))}
          />
        </div>
      }
    />
  );
}
